// T4 — THE CARD LIBRARY. A browsable, searchable grid of all 78 cards (our deck); clicking a card
// opens its page — full image + expert meanings (General, Love, Money, Yes/No), upright AND
// reversed. Meanings are generated once and stored server-side, so a revisit is instant and free.
//
// R60 MOVED IT OUT OF App.tsx, and the reason is this round's own: the component's two reads were
// the last of the fabricated-collection census, and its failure state said **"No card matches
// that."** — a sentence about a search box, over a library that never arrived. Both reads go through
// `src/lib/card-library.ts` now, which a gate RUNS against every answer the routes can give.
//
// It holds four pieces of state, so no gate can CALL it (R10-3's ruling: a stateful unit moves
// anyway when the law it carries needs an instrument, and here the law left with the transport).
// Its copy is bare English on purpose — see `cardLibraryNote`'s own note for the ruling.
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { TarotCardImage } from "./TarotCardImage";
import { listLibraryCards, fetchCardMeanings, cardLibraryNote } from "../lib/card-library";

export type CardLens = { general: string; love: string; money: string; yesNo: string };
export type CardMeanings = { upright: CardLens; reversed: CardLens };
export type LibraryCard = { slug: string; name: string; arcana: string; suit: string | null };

const LIBRARY_COPY = { signIn: "Sign in to open the card library.", failed: "The card library could not be loaded right now." };
const CARD_COPY = { signIn: "Sign in to open the card library.", failed: "This card could not be loaded right now." };

export default function TarotLibrary({ onClose }: { onClose: () => void }) {
  const [cards, setCards] = useState<LibraryCard[]>([]);
  const [busy, setBusy] = useState(true);
  const [listErr, setListErr] = useState("");
  const [q, setQ] = useState("");
  const [pick, setPick] = useState<LibraryCard | null>(null);
  const [meanings, setMeanings] = useState<CardMeanings | null>(null);
  const [cardBusy, setCardBusy] = useState(false);
  const [cardErr, setCardErr] = useState("");
  const [face, setFace] = useState<"upright" | "reversed">("upright");

  // **THE GUARD IS BEFORE THE PAINT AND THE ORDER IS THE LAW** (R52/R53/R55): the grid is only ever
  // filled from an answer that arrived, so a failed read cannot be drawn as a library with no cards
  // in it — the sentence below takes that screen instead.
  useEffect(() => {
    void listLibraryCards<LibraryCard>().then((answer) => {
      if (answer.ok) setCards(answer.cards);
      setListErr(cardLibraryNote(answer, LIBRARY_COPY));
      setBusy(false);
    });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") (pick ? setPick(null) : onClose()); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pick, onClose]);

  async function openCard(card: LibraryCard) {
    setPick(card); setMeanings(null); setCardErr(""); setFace("upright"); setCardBusy(true);
    const answer = await fetchCardMeanings<CardMeanings>(card.slug);
    if (answer.ok) setMeanings(answer.meanings);
    setCardErr(cardLibraryNote(answer, CARD_COPY));
    setCardBusy(false);
  }

  const shown = cards.filter((c) => !q.trim() || c.name.toLowerCase().includes(q.trim().toLowerCase()));
  const lens = meanings ? meanings[face] : null;

  if (typeof document === "undefined") return null;
  return createPortal(
    <div className="tarot-library-overlay" role="dialog" aria-label="Tarot card library" onMouseDown={() => (pick ? setPick(null) : onClose())}>
      <div className="tarot-library" onMouseDown={(e) => e.stopPropagation()}>
        <div className="tarot-library__head">
          <h3>{pick ? pick.name : "The Card Library"}</h3>
          {pick ? <button type="button" className="tarot-library__back" onClick={() => setPick(null)}>← All cards</button> : null}
          <button type="button" className="tarot-library__close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {!pick ? (
          <>
            <input className="tarot-library__search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search any of the 78 cards..." />
            {busy ? <p className="reading-status is-live">Opening the library...</p> : listErr ? <p className="reading-status" role="status">{listErr}</p> : (
              <div className="tarot-library__grid">
                {shown.map((c) => (
                  <button key={c.slug} type="button" className="tarot-library__card" onClick={() => openCard(c)}>
                    <TarotCardImage slug={c.slug} name={c.name} loading="lazy" />
                    <span>{c.name}</span>
                  </button>
                ))}
                {!shown.length ? <p className="reading-status">No card matches that.</p> : null}
              </div>
            )}
          </>
        ) : (
          <div className="tarot-card-page">
            <div className="tarot-card-page__art">
              <TarotCardImage slug={pick.slug} name={pick.name} orientation={face} />
            </div>
            <div className="tarot-card-page__body">
              <div className="tarot-card-page__toggle" role="tablist">
                {(["upright", "reversed"] as const).map((f) => (
                  <button key={f} type="button" role="tab" aria-selected={face === f} className={face === f ? "is-on" : ""} onClick={() => setFace(f)}>
                    {f === "upright" ? "Upright" : "Reversed"}
                  </button>
                ))}
              </div>
              {cardBusy ? <p className="reading-status is-live">Reading the card...</p> : cardErr ? <p className="reading-status">{cardErr}</p> : lens ? (
                <dl className="tarot-card-page__lenses">
                  <div><dt>General</dt><dd>{lens.general}</dd></div>
                  <div><dt>Love</dt><dd>{lens.love}</dd></div>
                  <div><dt>Money</dt><dd>{lens.money}</dd></div>
                  <div><dt>Yes / No</dt><dd>{lens.yesNo}</dd></div>
                </dl>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
